import React, { useState, useMemo } from 'react';
import PageTransition from '@/components/ui/PageTransition';
import { createModuleRouteGuard } from '@/modules/routeGuards';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { JsonEditor } from 'json-edit-react';
import { githubLightTheme } from '@json-edit-react/themes';
import { Card, Typography, Flex, Spin, Alert, Tag, Button, theme } from 'antd';
import {
  ExperimentOutlined,
  ReloadOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  NodeIndexOutlined,
  DatabaseOutlined
} from '@ant-design/icons';
import { PageHeader } from '@/components/ui/PageHeader';
import { StatCard } from '@/components/ui/StatCard';
import { StatCardGrid } from '@/components/ui/StatCardGrid';
import { useDemoModuleDataQuery } from '../api';

const { Text } = Typography;

export const Route = createFileRoute('/manage/modules/demoModule/')({
  component: PageComponent,
  beforeLoad: createModuleRouteGuard('demo-module')
});

export function PageComponent() {
  const [jsonData, setJsonData] = useState<any>(null);
  const { data, isLoading, error, refetch, isFetching } = useDemoModuleDataQuery();
  const { token } = theme.useToken();
  const { t } = useTranslation('demo-module');

  // Update local state when query data changes
  React.useEffect(() => {
    if (data) {
      setJsonData(data);
    }
  }, [data]);

  const handleDataChange = (newData: any) => {
    setJsonData(newData);
  };

  // Calculate data statistics
  const dataStats = useMemo(() => {
    if (!jsonData) return null;
    
    const countKeys = (obj: any): number => {
      if (typeof obj !== 'object' || obj === null) return 0;
      if (Array.isArray(obj)) return obj.length;
      return Object.keys(obj).length;
    };

    const countTotalKeys = (obj: any): number => {
      if (typeof obj !== 'object' || obj === null) return 0;
      if (Array.isArray(obj)) {
        return obj.reduce((sum: number, item: any) => sum + countTotalKeys(item), 0);
      }
      const keysCount = Object.keys(obj).length;
      const values = Object.values(obj) as any[];
      const valuesCount = values.reduce((sum: number, val: any): number => {
        return sum + countTotalKeys(val);
      }, 0);
      return keysCount + valuesCount;
    };

    return {
      topLevelKeys: countKeys(jsonData),
      totalKeys: countTotalKeys(jsonData),
      dataSize: JSON.stringify(jsonData).length,
      isArray: Array.isArray(jsonData),
      dataType: Array.isArray(jsonData) ? 'Array' : typeof jsonData === 'object' && jsonData !== null ? 'Object' : typeof jsonData
    };
  }, [jsonData]);

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <PageTransition animation="fadeInUp">
      <PageHeader
        icon={<ExperimentOutlined />}
        title={t('pageTitle')}
        description={t('pageDescription')}
        actions={
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            {t('refreshData')}
          </Button>
        }
      />

      {/* Loading State */}
      {isLoading && (
        <Card>
          <Flex vertical align="center" justify="center" gap={token.marginLG} style={{ padding: token.paddingXL * 2 }}>
            <Spin size="large" />
            <Text type="secondary">{t('loading')}</Text>
          </Flex>
        </Card>
      )}

      {/* Error State */}
      {error && (
        <Card>
          <Alert
            title={t('error.title')}
            description={
              <Flex vertical gap={token.marginMD} align="flex-start">
                <Text>{error instanceof Error ? error.message : t('error.fallback')}</Text>
                <Button type="primary" icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
                  {t('error.retry')}
                </Button>
              </Flex>
            }
            type="error"
            icon={<ExclamationCircleOutlined />}
            showIcon
          />
        </Card>
      )}

      {/* Data Display */}
      {!isLoading && !error && jsonData && (
        <>
          {dataStats && (
            <StatCardGrid>
              <StatCard label={t('stats.dataType')} value={dataStats.dataType} icon={<FileTextOutlined />} />
              <StatCard
                label={t('stats.topLevelKeys')}
                value={dataStats.topLevelKeys}
                icon={<CheckCircleOutlined />}
                color={token.colorSuccess}
              />
              <StatCard
                label={t('stats.totalKeys')}
                value={dataStats.totalKeys}
                icon={<NodeIndexOutlined />}
                color={token.colorInfo}
              />
              <StatCard
                label={t('stats.dataSize')}
                value={formatBytes(dataStats.dataSize)}
                icon={<DatabaseOutlined />}
                color={token.colorWarning}
              />
            </StatCardGrid>
          )}

          <Flex vertical gap={token.marginLG}>
            {/* JSON Editor Card */}
            <Card
              title={
                <Flex align="center" gap={token.marginXS}>
                  <FileTextOutlined style={{ color: token.colorPrimary }} />
                  <span>{t('editor.title')}</span>
                  <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                    {dataStats?.isArray ? t('editor.typeArray') : t('editor.typeObject')}
                  </Tag>
                </Flex>
              }
            >
              <Text type="secondary" style={{ display: 'block', marginBottom: token.marginSM }}>
                {t('editor.description')}
              </Text>
              <div
                style={{
                  border: `${token.lineWidth}px ${token.lineType} ${token.colorBorderSecondary}`,
                  borderRadius: token.borderRadiusLG,
                  padding: token.paddingMD,
                  backgroundColor: token.colorBgContainer,
                  minHeight: token.controlHeight * 12,
                  overflowX: 'auto'
                }}
              >
                <JsonEditor data={jsonData} setData={handleDataChange} theme={githubLightTheme} />
              </div>
            </Card>

            {/* Raw JSON Preview */}
            <Card
              title={
                <Flex align="center" gap={token.marginXS}>
                  <FileTextOutlined style={{ color: token.colorPrimary }} />
                  <span>{t('rawPreview.title')}</span>
                </Flex>
              }
            >
              <pre
                style={{
                  backgroundColor: token.colorFillQuaternary,
                  padding: token.padding,
                  borderRadius: token.borderRadiusLG,
                  overflow: 'auto',
                  maxHeight: token.controlHeight * 9,
                  fontSize: token.fontSizeSM,
                  fontFamily: token.fontFamilyCode,
                  margin: 0,
                  border: `${token.lineWidth}px ${token.lineType} ${token.colorBorderSecondary}`
                }}
              >
                {JSON.stringify(jsonData, null, 2)}
              </pre>
            </Card>
          </Flex>
        </>
      )}
    </PageTransition>
  );
};
